import React, { useEffect, useMemo, useRef } from 'react';
import { useActor } from '@xstate/react';
import { getHours } from 'date-fns';
import { FormContext, FormStateBasic } from 'informed';
import * as Yup from 'yup';

import { AuthCoreMachineRefType } from '@nw/auth-core';
import {
  DoChildMachineSelfTransition,
  OnClickGetOtpButton,
  userAuthHandlerMachineEvents,
  UserAuthHandlerMachineSendType,
  UserAuthHandlerMachineStateType,
  VerifyOtpButton
} from '@nw/client-auth-manager';
import {
  ButtonComponent,
  buttonSize,
  ButtonSize,
  buttonVariant,
  ButtonVariant,
  InformedInternationalPhoneNumberField
} from '@nw/design-system/omega';
import { OTPFieldBasedOnType, ResetAuthMachine } from '@nw/fields-manager';
import {
  allowedCountryCodeEnums,
  otpFieldTypeConstants
} from '@nw/shared/config';
import { LockIcon, RightArrow } from '@nw/shared/ui';
import {
  getURLSearchParam,
  isValidSixDigitOTP,
  otpRegularExpression,
  phoneNumberRegularExpression,
  sendGAEventToDataLayer
} from '@nw/shared/utils';

import DynamicTrustBasedMessages from './DynamicTrustBasedString';
import {
  AuthFormContainer,
  AuthFormContainerButton,
  ErrorText,
  GetOTPText,
  IconContainer,
  LoginPageFieldContainer,
  PhoneNumberFieldContainer,
  SecureNoteText,
  TermsAndConditionsText
} from './styledComponent';

interface LoginCardProps {
  onSuccessfulLogin: (
    userAuthHandlerMachineState: UserAuthHandlerMachineStateType
  ) => void;
  onFailedLogin: () => void;
  localConfig: any;
  shouldCallUserProfileDetails: boolean;
  userAuthHandlerMachineState: UserAuthHandlerMachineStateType;
  userAuthHandlerMachineSend: UserAuthHandlerMachineSendType;
}

interface RenderVerifyOtpButtonProps {
  formState: FormStateBasic;
  isNewUser: boolean;
  authMachineRef: AuthCoreMachineRefType;
  isAuthButtonLoading: boolean;
  userAuthHandlerMachineSend: UserAuthHandlerMachineSendType;
  userAuthHandlerMachineState: UserAuthHandlerMachineStateType;
  renderButtonContent?: () => JSX.Element;
}
const RenderVerifyOtpButton: React.FC<RenderVerifyOtpButtonProps> = ({
  formState,
  isNewUser,
  authMachineRef,
  isAuthButtonLoading,
  userAuthHandlerMachineSend,
  userAuthHandlerMachineState,
  renderButtonContent
}) => {
  const isVerifyAndSignUpButtonDisabled = (): boolean => {
    if (isNewUser) {
      return !isValidSixDigitOTP(values['otp'] as string);
    } else return !isValidSixDigitOTP(values['otp'] as string);
  };
  const { values } = formState;

  const [authCoreMachineState, authCoreMachineSend] = useActor(authMachineRef);

  const { otpFieldRef } = authCoreMachineState.context;

  if (!otpFieldRef) return <></>;
  return (
    <VerifyOtpButton
      otpFieldRef={otpFieldRef}
      isVerifyAndSignUpButtonDisabled={isVerifyAndSignUpButtonDisabled()}
      otp={values['otp'] as string}
      buttonId={'verifyOtpButton'}
      buttonText={'Verify & Login'}
      isButtonLoading={isAuthButtonLoading}
      userAuthHandlerMachineSend={userAuthHandlerMachineSend}
      userAuthHandlerMachineState={userAuthHandlerMachineState}
      renderButtonContent={renderButtonContent}
      shouldShrinkButtonWhileLoading={false}
    />
  );
};
const t = (key: string) => key;

const LoginCard: React.FC<LoginCardProps> = ({
  onSuccessfulLogin,
  userAuthHandlerMachineState,
  userAuthHandlerMachineSend
}) => {
  const phoneNumberField = 'phoneNumber';
  const loginOtpField = 'otp';

  const validationSchemaObject = useMemo(
    () =>
      Yup.object({
        phoneNumber: Yup.string()
          .required('Required')
          .matches(
            phoneNumberRegularExpression,
            'Please enter a valid phone number'
          ),
        otp: Yup.string()
          .matches(otpRegularExpression, 'Please enter a valid OTP')
          .required('Required')
      }),
    []
  );

  const {
    values: contextValues,
    isOtpSentToUser,
    shouldDoChildMachineSelfTransition,
    authMachineRef,
    isNewUser,
    isAuthButtonLoading,
    isLoggedIn,
    errorMessage
  } = userAuthHandlerMachineState.context;

  // Track if we've already posted the loggedin message
  const hasPostedLoggedInMessage = useRef(false);

  // Post loggedin message when OTP is verified (isLoggedIn becomes true)
  useEffect(() => {
    if (isLoggedIn && !hasPostedLoggedInMessage.current) {
      window.postMessage('loggedin', '*');
      hasPostedLoggedInMessage.current = true;
    }
  }, [isLoggedIn]);

  const getInitialValues = (field: string) => {
    if (contextValues[field]) {
      return contextValues[field];
    }
    return '';
  };

  const renderEditOtpButton = (field: string): React.ReactElement => {
    const isGetOtpButtonLoading =
      userAuthHandlerMachineState.context.authMachineRef?.state.context
        .isGetOtpButtonLoading;

    const onClickEditOtpButton = () => {
      sendGAEventToDataLayer({
        event: 'edit_phone_number'
      });
      userAuthHandlerMachineSend({
        type: userAuthHandlerMachineEvents.resetAuthMachine
      } as ResetAuthMachine);
    };

    return (
      <ButtonComponent
        key={field}
        id={field}
        isDisabled={false}
        isLoading={isGetOtpButtonLoading}
        type={'button'}
        onClick={onClickEditOtpButton}
        variant={'Primary'}
        size={'Small'}
        className={`getOTPFieldButtonClassName`}
      >
        <GetOTPText>Edit</GetOTPText>
      </ButtonComponent>
    );
  };

  const renderPhoneNumberField = (formProps: FormContext) => {
    const { formState } = formProps;
    const { values, touched, errors } = formState;
    const fieldValidations = {
      validateOnBlur: true,
      validateOnChange: true
    };

    return (
      <PhoneNumberFieldContainer>
        <InformedInternationalPhoneNumberField
          label={'Mobile number'}
          placeholder={'Enter Number'}
          field={phoneNumberField}
          values={values}
          touched={touched[phoneNumberField]}
          error={errors[phoneNumberField]}
          validationSchemaObject={validationSchemaObject}
          isDisabled={isAuthButtonLoading || isOtpSentToUser}
          autoFocus={true}
          popOverStyle={'secondary'}
          t={t}
          fieldValidations={fieldValidations}
          allowedCountriesList={allowedCountryCodeEnums}
          wrapperWithPopOverStyles={'phoneNumberFieldPopOverStyles'}
          intlPhoneNumberFieldStyles={'phoneNumberContainerStyles'}
          labelStyles={'phoneNumberFieldLabelStyles'}
          phoneNumberInputStyles={'phoneNumberFieldInputStyles'}
          isMarginBottomZero={true}
        />
        {isOtpSentToUser && renderEditOtpButton(phoneNumberField)}
      </PhoneNumberFieldContainer>
    );
  };

  const onClickGetOtpButton = (values: FormStateBasic['values']) => {
    if (shouldDoChildMachineSelfTransition) {
      userAuthHandlerMachineSend({
        type: userAuthHandlerMachineEvents.doChildMachineSelfTransition
      } as DoChildMachineSelfTransition);
    } else
      userAuthHandlerMachineSend({
        type: userAuthHandlerMachineEvents.onClickGetOtpButton,
        values: {
          phoneNumber: values[phoneNumberField]
        }
      } as OnClickGetOtpButton);
  };

  const isGetOtpButtonDisabled = (
    errors: FormStateBasic['errors'],
    values: FormStateBasic['values']
  ) => {
    if (
      errors[phoneNumberField] ||
      !values[phoneNumberField] ||
      values[phoneNumberField].toString().length < 10
    ) {
      return true;
    }
    return false;
  };

  const renderButtonContent = (text: string) => {
    return (
      <>
        <GetOTPText>{text}</GetOTPText>
        <IconContainer>
          <RightArrow fill={'#ffffff'} isSolid={true} />
        </IconContainer>
      </>
    );
  };

  const renderGetOtpButton = (
    errors: FormStateBasic['errors'],
    values: FormStateBasic['values']
  ) => {
    return (
      <AuthFormContainerButton
        id={'getOTPButton'}
        isLoading={isAuthButtonLoading}
        variant={buttonVariant.primary as ButtonVariant}
        size={buttonSize.large as ButtonSize}
        type={'submit'}
        className={'getOTPButtonStyles'}
        isDisabled={isGetOtpButtonDisabled(errors, values)}
        onClick={() => onClickGetOtpButton(values)}
        shouldShrinkButtonWhileLoading={false}
      >
        {renderButtonContent('Get OTP')}
      </AuthFormContainerButton>
    );
  };

  const getDynamicSeats = () => {
    const baseSeats = [
      28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17, 16, 15, 14, 13, 12, 11
    ];
    const startHour = 6;
    const currentHour = getHours(new Date());
    // Calculate the time slot index (cycling over 24 hours)
    const relativeHour = (currentHour - startHour + 24) % 24;
    const index = Math.floor((relativeHour / 24) * baseSeats.length);

    const currentSeats = baseSeats[index];
    return currentSeats;
  };

  const renderPhoneOtpField = (formState: FormStateBasic) => {
    const field = loginOtpField;

    const fieldData = {
      field: field,
      otpInputType: otpFieldTypeConstants.sixDigitInputBoxes
    };

    const dynamicSeatsText = `🔔 Only <strong>${getDynamicSeats()} seats</strong> left for the early NAT round.`;

    return (
      <>
        <OTPFieldBasedOnType
          fieldData={fieldData as any}
          registrationFormState={userAuthHandlerMachineState as any}
          authMachineRef={authMachineRef}
          validationSchemaObject={validationSchemaObject}
          informedFieldsInfo={formState as any}
          enableAutoVerifyForSixDigitOTP={true}
        />
        <DynamicTrustBasedMessages
          nudges={[
            dynamicSeatsText,
            '🗺️ Students from <strong>28 states</strong> and <strong>300+ cities</strong> appeared in Phase 1.',
            '🏆 Top rankers from <strong>3000+ colleges</strong> appeared in Phase 1 last year.'
          ]}
        />
      </>
    );
  };

  const showSecuredText = () => {
    const urlParams = getURLSearchParam();
    const piParam = urlParams.get('pi');
    return piParam === 'dm';
  };

  const renderTermsAndConditionsText = () => {
    return (
      <TermsAndConditionsText setMargin={showSecuredText()}>
        By creating an account I have read and agree to&nbsp;
        <a
          href="https://www.niatindia.com/terms-and-conditions-2025"
          target="_blank"
          className="paymentTerms"
          referrerPolicy="no-referrer-when-downgrade"
        >
          T&C
        </a>
        &nbsp;and&nbsp;
        <a
          href="https://ccbp.in/privacy-policy"
          target="_blank"
          className="paymentTerms"
          referrerPolicy="no-referrer-when-downgrade"
        >
          Privacy Policy
        </a>
      </TermsAndConditionsText>
    );
  };

  const renderAuthFormContainer = (): React.ReactElement => {
    if (isLoggedIn) {
      onSuccessfulLogin(userAuthHandlerMachineState);
    }

    return (
      <AuthFormContainer
        allowEmptyStrings={true}
        className={'authFormContainer'}
        initialValues={{
          otp: getInitialValues('otp'),
          phoneNumber: getInitialValues('phoneNumber'),
          signUpTermsAndConditionsCheckbox: true
        }}
      >
        {(formProps: FormContext) => {
          const { formState } = formProps;
          const { values, touched, errors } = formState;
          return (
            <>
              {renderPhoneNumberField(formProps)}
              {isOtpSentToUser && renderPhoneOtpField(formState)}
              <ErrorText className="login-error-message-text">
                {errorMessage}
              </ErrorText>
              {!isOtpSentToUser && renderGetOtpButton(errors, values)}
              {isOtpSentToUser && (
                <RenderVerifyOtpButton
                  formState={formState}
                  isNewUser={isNewUser}
                  authMachineRef={authMachineRef}
                  isAuthButtonLoading={isAuthButtonLoading}
                  userAuthHandlerMachineSend={userAuthHandlerMachineSend}
                  userAuthHandlerMachineState={userAuthHandlerMachineState}
                  renderButtonContent={() =>
                    renderButtonContent('Book my NAT Slot')
                  }
                />
              )}
              {!isOtpSentToUser && !showSecuredText() && (
                <SecureNoteText>
                  <LockIcon />
                  Your information is 100% secure with us.
                </SecureNoteText>
              )}
              {renderTermsAndConditionsText()}
            </>
          );
        }}
      </AuthFormContainer>
    );
  };

  return (
    <>
      <LoginPageFieldContainer>
        {renderAuthFormContainer()}
      </LoginPageFieldContainer>
    </>
  );
};

export default LoginCard;
